import { useCallback, useContext, useEffect, useRef, useState } from 'react';

const REPORT_POLLING_INTERVAL_MS = 5 * 60 * 1000;

import { WebSocketContext } from '../../websocket-context/websocket-provider';
import {
  IPortfolioPerformanceAccountReport,
  IPortfolioPerformanceAccounts,
  IPortfolioPerformancePosition,
} from '../portfolio-performance.types';

const getDisplayValue = (source: Record<string, any> | undefined, key: string) => {
  const item = source?.[key];

  if (!item) return null;
  if (typeof item === 'object' && 'display' in item) return item.display ?? null;

  return item;
};

const toNumberOrNull = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

// source.positions is a dict keyed by instrument, but also carries two
// pseudo-keys mixed into the same object: "total" (position/mark_price are
// empty strings, only pv is real) and "calculated_at" (a plain timestamp
// string, not a position at all) — both are pulled out here rather than
// iterated as instrument rows.
const parsePositions = (
  raw: unknown,
): { positions: IPortfolioPerformancePosition[]; totalPv: number | null } => {
  if (!raw || typeof raw !== 'object') return { positions: [], totalPv: null };

  const positions: IPortfolioPerformancePosition[] = [];
  let totalPv: number | null = null;

  for (const [instrument, value] of Object.entries(raw as Record<string, unknown>)) {
    if (instrument === 'calculated_at') continue;
    if (!value || typeof value !== 'object') continue;
    const row = value as Record<string, unknown>;

    if (instrument === 'total') {
      totalPv = toNumberOrNull(row.pv);
      continue;
    }

    const position = toNumberOrNull(row.position);
    if (position === null) continue;

    positions.push({
      instrument,
      position,
      averagePrice: toNumberOrNull(row.average_price),
      markPrice: toNumberOrNull(row.mark_price),
      pv: toNumberOrNull(row.pv) ?? 0,
    });
  }

  return { positions, totalPv };
};

export const normalizePortfolioPerformanceReport = (
  report: any,
): IPortfolioPerformanceAccountReport => {
  const source = Array.isArray(report) ? report[0] || {} : report?.report || report || {};
  const account = source.account || {};
  const metrics = source.metrics || {};
  const { positions, totalPv } = parsePositions(source.positions);

  return {
    team: source.manager ?? null,
    strategy: account.strategy ?? null,
    description: account.description || null,
    positions,
    positionsTotalPv: totalPv,
    capital: getDisplayValue(account, 'initial_capital'),
    npv: getDisplayValue(account, 'pv'),
    daily: getDisplayValue(metrics, 'daily_return'),
    mtd: getDisplayValue(metrics, 'mtd_return'),
    ytd: getDisplayValue(metrics, 'ytd_return'),
    maxDD: getDisplayValue(metrics, 'max_dd'),
    sharpe: getDisplayValue(metrics, 'sharpe_ratio'),
    sortino: getDisplayValue(metrics, 'sortino_ratio'),
    denomination: source.denomination ?? account.currency ?? null,
    historical: source.historical ?? null,
    ytdReturnRaw: typeof metrics.ytd_return?.value === 'number' ? metrics.ytd_return.value : null,
    dailyReturnRaw:
      typeof metrics.daily_return?.value === 'number' ? metrics.daily_return.value : null,
    mtdReturnRaw: typeof metrics.mtd_return?.value === 'number' ? metrics.mtd_return.value : null,
    pvRaw: typeof account.pv?.value === 'number' ? account.pv.value : null,
    sharpeRaw: typeof metrics.sharpe_ratio?.value === 'number' ? metrics.sharpe_ratio.value : null,
    sortinoRaw:
      typeof metrics.sortino_ratio?.value === 'number' ? metrics.sortino_ratio.value : null,
    maxDDRaw: typeof metrics.max_dd?.value === 'number' ? metrics.max_dd.value : null,
    calculatedAt: typeof source.calculated_at === 'string' ? source.calculated_at : null,
  };
};

export const usePortfolioPerformanceReport = () => {
  const { sendMessage, subscribeToChannel, isConnected, isReconnection } =
    useContext(WebSocketContext);
  const [reportsByAccount, setReportsByAccount] = useState<
    Record<string, IPortfolioPerformanceAccountReport>
  >({});
  const [loadingReportsByAccount, setLoadingReportsByAccount] = useState<Record<string, boolean>>(
    {},
  );
  const [errorReportsByAccount, setErrorReportsByAccount] = useState<Record<string, boolean>>({});
  const intervalsRef = useRef<Record<string, number>>({});
  const trackedAccountsRef = useRef<IPortfolioPerformanceAccounts[]>([]);

  const fetchReport = useCallback(
    (account: IPortfolioPerformanceAccounts) => {
      if (!isConnected) return;

      const accountName = account.name;
      const channelId = `PP_REPORT_${accountName.replace(/\s+/g, '_').toUpperCase()}`;

      if (!trackedAccountsRef.current.some((a) => a.name === accountName)) {
        trackedAccountsRef.current = [...trackedAccountsRef.current, account];
      }

      setLoadingReportsByAccount((prev) => ({ ...prev, [accountName]: true }));
      setErrorReportsByAccount((prev) => ({ ...prev, [accountName]: false }));

      const doFetch = () => {
        sendMessage(
          JSON.stringify({
            jsonrpc: '2.0',
            id: channelId,
            method: 'portfolio_performance.report',
            params: { account: accountName },
          }),
        );

        subscribeToChannel(channelId, (resp: any) => {
          setLoadingReportsByAccount((prev) => ({ ...prev, [accountName]: false }));

          if (resp?.error) {
            setErrorReportsByAccount((prev) => ({ ...prev, [accountName]: true }));
            return;
          }

          if (resp?.result) {
            setErrorReportsByAccount((prev) => ({ ...prev, [accountName]: false }));
            setReportsByAccount((prev) => ({
              ...prev,
              [accountName]: normalizePortfolioPerformanceReport(resp.result),
            }));
          }
        });
      };

      doFetch();

      if (intervalsRef.current[accountName]) {
        clearInterval(intervalsRef.current[accountName]);
      }
      intervalsRef.current[accountName] = window.setInterval(doFetch, REPORT_POLLING_INTERVAL_MS);
    },
    [isConnected, sendMessage, subscribeToChannel],
  );

  useEffect(() => {
    if (!isConnected || isReconnection) {
      Object.values(intervalsRef.current).forEach(clearInterval);
      intervalsRef.current = {};
      return;
    }

    if (trackedAccountsRef.current.length > 0) {
      Object.values(intervalsRef.current).forEach(clearInterval);
      intervalsRef.current = {};
      trackedAccountsRef.current.forEach(fetchReport);
    }

    return () => {
      Object.values(intervalsRef.current).forEach(clearInterval);
      intervalsRef.current = {};
    };
  }, [isConnected, isReconnection, fetchReport]);

  return {
    reportsByAccount,
    loadingReportsByAccount,
    errorReportsByAccount,
    fetchReport,
  };
};
